import { type CustomFieldEditor, type CustomFieldKind } from '@cheqroom/api';
import { FC, ReactNode } from 'react';

import { TextComparator } from './Filters/Text/Text';

/**
 * The views an overview can render. Single source of truth: `ResourceDataView` re-exports this,
 * and `use-overview-config.hook` / `ViewSwitcher` import it rather than redeclaring their own union.
 */
export const VIEW_TYPES = ['table', 'cards', 'scheduler', 'list'] as const;
export type ViewType = (typeof VIEW_TYPES)[number];

export type InlineField = {
	id: string;
	value: unknown;
	type?: CustomFieldEditor;
	kind?: CustomFieldKind | null;
	icon?: FC | ReactNode | undefined;
};

export type Action<ActionType, EntityType> = {
	id: ActionType;
	label: (string | null) | ((entity: EntityType, isBulk: boolean) => string);
	onClick?: (entity: EntityType) => void;
	onBulkClick?: (entities: EntityType[]) => void;
	available?: ((entity: EntityType) => boolean) | boolean;
	bulkAvailable?: (entities: EntityType[]) => boolean;
	destructive?: boolean;
	disabledMessage?: (entities: EntityType[]) => string | null | undefined;
	tooltip?: string;
};

export type MappedAction<ActionType> = {
	id: ActionType;
	label: string;
	onClick: () => void;
	destructive?: boolean;
	disabled?: boolean;
	disabledMessage?: () => string | null | undefined;
	tooltip?: string;
};

export type ColumnConfig<Field> = {
	id: Field;
	name: string;
	kind: CustomFieldKind;
	editor: CustomFieldEditor;
	unit: string | null;
	allowedComparators?: TextComparator[];
	excludeFromFilters?: boolean;
	basicField?: boolean;
	select?: string[];
};

export type OverviewConfig = {
	view: ViewType;
	pageSize: number;
};

export type OverviewViewConfig<Field> = {
	fields: Field[];
	inlineFields: Field[];
};

export type NamespacedOverviewViewConfig<Field> = Record<string, OverviewViewConfig<Field>>;
